// Pure, DOM-free, clock-free commons mirror math. Both the You/Now cards and the
// transparency page consume these. Currency is never converted here — we sum hostValue.

export type CostEvent = {
  period: { start: string; end: string };
  spendFiat: number;
  fiatCurrency: string;
  activeAccounts: number;
  accountsBasis: string;
  breakdown?: { category: string; amount: number; oneTime: boolean }[];
};

export type ContribEvent = {
  beneficiary: string;
  rail: string;
  nativeAmount: string;
  nativeUnit: string;
  pegValue: number;
  pegCurrency: string;
  hostValue: number;
  hostCurrency: string;
  hostValueApprox: boolean;
  fxRate?: number;
  fxAt?: string;
  observedAt: string;
};

export type PeriodPoint = {
  period: { start: string; end: string };
  cost: number; // recurring only
  oneTime: number;
  contributed: number;
  net: number; // contributed - recurring cost; >0 surplus, <0 deficit
};

export type Totals = {
  totalSpend: number; // recurring
  totalOneTime: number;
  totalContributed: number;
  deficit: number; // totalSpend - totalContributed; >0 deficit, <0 surplus
};

export type Projection = {
  projectedContributed: number;
  recurringCost: number;
  method: "pro-rata-linear";
};

/** Parse an ISO/RFC3339 timestamp to micros since epoch. `new Date(value)` (with an
 *  argument) is allowed — it formats a received timestamp, it does not read the wall clock. */
export function microsOf(iso: string): number {
  return Date.parse(iso) * 1000;
}

export function recurringSpend(c: CostEvent): number {
  if (c.breakdown && c.breakdown.length > 0) {
    return c.breakdown.filter((l) => !l.oneTime).reduce((s, l) => s + l.amount, 0);
  }
  return c.spendFiat;
}

export function oneTimeSpend(c: CostEvent): number {
  if (c.breakdown && c.breakdown.length > 0) {
    return c.breakdown.filter((l) => l.oneTime).reduce((s, l) => s + l.amount, 0);
  }
  return 0;
}

export function fairShare(c: CostEvent): number {
  return recurringSpend(c) / Math.max(c.activeAccounts, 1);
}

/** A contribution belongs to a period if observedAt is in [start, end). */
function inPeriod(observedAt: string, p: { start: string; end: string }): boolean {
  const t = microsOf(observedAt);
  return t >= microsOf(p.start) && t < microsOf(p.end);
}

export function periodSeries(costs: CostEvent[], contribs: ContribEvent[]): PeriodPoint[] {
  const sorted = [...costs].sort((a, b) => microsOf(a.period.start) - microsOf(b.period.start));
  return sorted.map((c) => {
    const contributed = contribs
      .filter((x) => inPeriod(x.observedAt, c.period))
      .reduce((s, x) => s + x.hostValue, 0);
    const cost = recurringSpend(c);
    return { period: c.period, cost, oneTime: oneTimeSpend(c), contributed, net: contributed - cost };
  });
}

export function yourCostSoFar(costs: CostEvent[], accountCreatedAtMicros: number): number {
  return costs
    .filter((c) => microsOf(c.period.start) >= accountCreatedAtMicros)
    .reduce((s, c) => s + fairShare(c), 0);
}

export function yourGivenSoFar(contribs: ContribEvent[], myDid: string): number {
  return contribs.filter((x) => x.beneficiary === myDid).reduce((s, x) => s + x.hostValue, 0);
}

/** Has this account chipped in during the *current* funding period? "Current" = the latest
 *  cost event's period (the run-rate window the card shows). When no cost periods exist yet,
 *  any contribution credited to me counts. Drives the Now-tab single-button state. */
export function chippedInThisPeriod(costs: CostEvent[], contribs: ContribEvent[], myDid: string): boolean {
  const mine = contribs.filter((x) => x.beneficiary === myDid);
  if (mine.length === 0) return false;
  if (costs.length === 0) return true; // no periods yet — any of my gifts counts
  const latest = [...costs].sort((a, b) => microsOf(b.period.start) - microsOf(a.period.start))[0];
  return mine.some((x) => inPeriod(x.observedAt, latest.period));
}

export function commonsTotals(costs: CostEvent[], contribs: ContribEvent[]): Totals {
  const totalSpend = costs.reduce((s, c) => s + recurringSpend(c), 0);
  const totalOneTime = costs.reduce((s, c) => s + oneTimeSpend(c), 0);
  const totalContributed = contribs.reduce((s, x) => s + x.hostValue, 0);
  return { totalSpend, totalOneTime, totalContributed, deficit: totalSpend - totalContributed };
}

export function observedAccountFloor(contribs: ContribEvent[], period: { start: string; end: string }): number {
  const dids = new Set(contribs.filter((x) => inPeriod(x.observedAt, period)).map((x) => x.beneficiary));
  return dids.size;
}

export function projectCurrentPeriod(
  costs: CostEvent[],
  contribs: ContribEvent[],
  nowMicros: number,
): Projection | null {
  if (costs.length === 0) return null;
  const latest = [...costs].sort((a, b) => microsOf(b.period.start) - microsOf(a.period.start))[0];
  const start = microsOf(latest.period.start);
  const end = microsOf(latest.period.end);
  const contributed = contribs
    .filter((x) => inPeriod(x.observedAt, latest.period))
    .reduce((s, x) => s + x.hostValue, 0);
  const elapsed = Math.max(nowMicros - start, 1);
  const span = Math.max(end - start, 1);
  const frac = Math.min(elapsed / span, 1);
  const projectedContributed = frac > 0 ? contributed / frac : contributed;
  return { projectedContributed, recurringCost: recurringSpend(latest), method: "pro-rata-linear" };
}
